import { SANGRE } from '../ui/HudCombat.js';

import { worldToScreen, type IsoTileSize } from './iso.js';

import type { TelegraphArea, TelegraphStartedPayload } from '@trames/shared/schemas';
import type Phaser from 'phaser';

/**
 * Decals server-side de telegraph (TKT-029).
 *
 * Pintamos en el suelo iso un polígono (circle aprox., cone con sector) con
 * opacidad 60% magenta. Cerca del resolveTick intensificamos visualmente.
 *
 * El timing usa `serverTime` que llega en el envelope ts (no tickId) para
 * evitar drift con interpolación del cliente (riesgo §spike 6).
 */

interface ActiveDecal {
  telegraphId: string;
  area: TelegraphArea;
  startServerTime: number;
  resolveServerTime: number;
  graphics: Phaser.GameObjects.Graphics;
}

export class DecalLayer {
  private scene: Phaser.Scene;
  private tile: IsoTileSize;
  private active = new Map<string, ActiveDecal>();
  private serverClock: () => number;
  /** Offset en pantalla del world (0,0). Lo aporta GameScene. */
  private origin: { x: number; y: number };

  constructor(
    scene: Phaser.Scene,
    tile: IsoTileSize,
    serverClock: () => number = () => Date.now(),
    origin: { x: number; y: number } = { x: 0, y: 0 },
  ) {
    this.scene = scene;
    this.tile = tile;
    this.serverClock = serverClock;
    this.origin = origin;
  }

  setOrigin(origin: { x: number; y: number }): void {
    this.origin = origin;
  }

  add(payload: TelegraphStartedPayload, ts: number): void {
    if (this.active.has(payload.telegraphId)) return;
    const g = this.scene.add.graphics();
    g.setDepth(0); // bajo los sprites de jugadores.
    // Convertimos startTick/resolveTick a tiempo de pared usando ts de la
    // emisión y la cadencia 30 Hz (33.33 ms/tick).
    const tickDurationMs = 1000 / 30;
    const startServerTime = ts + (payload.startTick - payload.resolveTick) * tickDurationMs;
    const resolveServerTime = ts;
    // En realidad, el ts viene asociado al envío al startTick (broadcast
    // emitido al startTick). Para la duración del decal usamos:
    const elapsed = payload.resolveTick - payload.startTick;
    void elapsed;
    this.active.set(payload.telegraphId, {
      telegraphId: payload.telegraphId,
      area: payload.area,
      startServerTime,
      resolveServerTime,
      graphics: g,
    });
  }

  remove(telegraphId: string): void {
    const d = this.active.get(telegraphId);
    if (!d) return;
    d.graphics.destroy();
    this.active.delete(telegraphId);
  }

  update(): void {
    const now = this.serverClock();
    for (const decal of this.active.values()) {
      const total = decal.resolveServerTime - decal.startServerTime;
      const elapsed = now - decal.startServerTime;
      const t = total > 0 ? Math.max(0, Math.min(1, elapsed / total)) : 1;
      this.renderArea(decal, t);
    }
  }

  private renderArea(decal: ActiveDecal, t: number): void {
    const g = decal.graphics;
    g.clear();
    const alpha = 0.35 + 0.3 * t; // intensifica cerca del resolve.
    const color = hexToNumber(SANGRE);
    if (decal.area.shape === 'circle') {
      const raw = worldToScreen(decal.area.center, this.tile);
      const s = { x: raw.x + this.origin.x, y: raw.y + this.origin.y };
      g.fillStyle(color, alpha);
      g.lineStyle(2, color, Math.min(1, alpha + 0.2));
      // Aproximamos el radio iso con un círculo simple.
      const r = decal.area.radius * (this.tile.width / 2);
      g.fillCircle(s.x, s.y, r);
      g.strokeCircle(s.x, s.y, r);
    } else {
      // Cone: dibujamos un sector aproximado con N segmentos.
      const rawOrigin = worldToScreen(decal.area.origin, this.tile);
      const origin = { x: rawOrigin.x + this.origin.x, y: rawOrigin.y + this.origin.y };
      const r = decal.area.radius * (this.tile.width / 2);
      const forwardAngle = Math.atan2(decal.area.forward.y, decal.area.forward.x);
      const half = (decal.area.angleDeg / 2) * (Math.PI / 180);
      const segments = 16;
      g.fillStyle(color, alpha);
      g.lineStyle(2, color, Math.min(1, alpha + 0.2));
      g.beginPath();
      g.moveTo(origin.x, origin.y);
      for (let i = 0; i <= segments; i++) {
        const a = forwardAngle - half + (2 * half * i) / segments;
        // Aplica la misma proyección iso: y se aplasta por (tile.height/tile.width).
        const x = origin.x + Math.cos(a) * r;
        const y = origin.y + Math.sin(a) * r * (this.tile.height / this.tile.width);
        g.lineTo(x, y);
      }
      g.closePath();
      g.fillPath();
      g.strokePath();
    }
  }

  destroy(): void {
    for (const d of this.active.values()) d.graphics.destroy();
    this.active.clear();
  }
}

function hexToNumber(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}
